import React, { useMemo } from 'react'
import Link from 'components/Link'
import { Button } from '@mui/material'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { JobFooter_Job$key } from '__generated__/JobFooter_Job.graphql'
import { JobFooter_User$key } from '__generated__/JobFooter_User.graphql'
import { useIsClient } from 'hooks/user'
import { JobFooter_JobApplication$key } from '__generated__/JobFooter_JobApplication.graphql'
import ButtonStack from 'components/ButtonStack'
import Condition from 'components/Condition'
import useStyles from './styles'

export interface IJobFooterProps {
  jobFragmentRef: JobFooter_Job$key
  userFragmentRef: JobFooter_User$key
  apply: () => void
  fakeApply: () => void
}

export default function JobFooter({ jobFragmentRef, userFragmentRef, apply, fakeApply }: IJobFooterProps) {
  const { classes } = useStyles()
  const user = useFragment(graphql`
    fragment JobFooter_User on User {
      status

      ...user_useIsClient
    }
  `, userFragmentRef)
  const client = useIsClient(user)

  const job = useFragment(graphql`
    fragment JobFooter_Job on Job {
      incognito

      firm {
        slug
        name
      }
      jobApplication {
        ...JobFooter_JobApplication
      }
    }
  `, jobFragmentRef)

  const jobApplication = useFragment<JobFooter_JobApplication$key>(graphql`
    fragment JobFooter_JobApplication on JobApplication {
      status
    }
  `, job?.jobApplication)

  const showApplyButton = useMemo(() => {
    if (user?.status !== 'pending' && jobApplication?.status === 'job_application_sent') {
      return false
    }

    return true
  }, [jobApplication, user])

  const showCompany = !job?.incognito

  if (!job?.firm) return null

  return (
    <ButtonStack className={classes.footer} variant="default">
      <Condition condition={showCompany}>
        <Link href={`/${job.firm.slug}`} className={classes.link}>
          <Button color="secondary">
            Browse more jobs from {job.firm.name}
          </Button>
        </Link>
      </Condition>

      {client ? (
        <Button
          color="primary"
          onClick={fakeApply}
          data-cy="apply-for-job-fake"
        >
          Apply now
        </Button>
      ) : (
        showApplyButton ? (
          <Button
            color="primary"
            onClick={apply}
            data-cy="apply-for-job"
          >
            Apply now
          </Button>
        ) : null
      )}
    </ButtonStack>
  )
}
